import { useEffect, useCallback, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { BottomNav } from './BottomNav.js';
import { shouldHideBottomNav } from './bottomNavActiveTab.js';
import { useTenantCode } from '../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../shared/session/CustomerSessionProvider.js';
import { useAccountMe } from '../features/account/accountApi.js';
import { RequireContactVerificationComplete } from '../features/account/routing/RequireContactVerificationComplete.js';
import { MoreShellProvider, useMoreShell } from './MoreShellContext.js';
import { MoreDrawer } from '../features/more/MoreDrawer.js';
import { isAccountRoute, isMoreRoute } from '../features/more/moreRouteUtils.js';
import { CUSTOMER_CONTENT_WIDTH_CLASS } from './customerTopBarStyles.js';
import { CustomerShellChromeProvider } from './CustomerShellChromeContext.js';
import { CustomerShellHeader } from './CustomerShellHeader.js';
import { DiscoveryShellLocationProvider } from './DiscoveryShellLocationContext.js';
import { ShellDepthBackProvider } from './ShellDepthBackContext.js';
import { ShellPlaceTitleProvider } from './ShellPlaceTitleContext.js';
import { useShellDepthBack } from './useShellDepthBack.js';
import {
  RemountBoundary,
  RouteErrorFallback,
} from '../shared/components/ErrorBoundary/index.js';
import { isCustomerRecoveryPath } from '../shared/recovery/index.js';
import { ErrorIsolationProbe } from '../test/e2e/errorIsolationProbe.js';
import { E2eNavigateBridgeMount } from './E2eNavigateBridgeMount.js';
import { useHierarchicalSystemBack } from './useHierarchicalSystemBack.js';
import { useBrowseVisitRecorder } from './useBrowseVisitRecorder.js';
import { useOidcPostAuthReturnRestore } from './useOidcPostAuthReturnRestore.js';
import { usePostPayPendingAuthActionRunner } from '../features/post-pay/usePostPayPendingAuthActionRunner.js';
import { useOidcPopupChildHandoff, useOidcPopupOpenerResume } from './useOidcPopupBridge.js';
import { useDirectoryUiPreferenceHydrate } from '../features/account/useDirectoryUiPreferenceSync.js';
import { HierarchicalBackFlowProvider } from '../shared/context/HierarchicalBackFlowContext.js';
import {
  OverlayBackDismissProvider,
  useOptionalOverlayBackDismiss,
} from '../shared/context/OverlayBackDismissContext.js';
import { ToastHost } from '../shared/ui/ToastHost.js';
import { ConsentSyncFailedBanner } from './ConsentSyncFailedBanner.js';
import { CustomerOfflineBanner } from './CustomerOfflineBanner.js';
import { CustomerTestEconomyBanner } from './CustomerTestEconomyBanner.js';
import { CustomerPullToRefresh } from './pwa/PullToRefreshIndicator.js';
import { CustomerDesktopSidebar } from './CustomerDesktopSidebar.js';
import { resolveHubEmptyCartConsumeAction } from './hubEmptyCartIntent.js';
import { APP_BUILD_LABEL } from '../shared/appBuildLabel.js';
import { AppShellChromeFooter } from 'pi-kiosk-shared/ui';

/** Matches sticky-cart overlay clearance above BottomNav. */
const BOTTOM_CHROME_CLEARANCE = 'calc(4.75rem + env(safe-area-inset-bottom))';

/**
 * Bottom chrome + sticky cart clearance + 32px content buffer so the last
 * product never sits under the floating cart bar (Retail V1 cart bar spec).
 *
 * Nav-visible pb derives from `--customer-bottom-chrome` (same 4.75rem +
 * env(safe-area-inset-bottom) stack BottomNav uses). Fallback keeps safe-area
 * in the class string for MOB-01 / before the shell effect runs. hide-nav
 * (recovery/*, not sign-in) uses MAIN_BOTTOM_PADDING_HIDE_NAV — one Tailwind
 * pb with max(1rem, env()) + sticky + 2rem. Never pair that calc with
 * customer-safe-area-bottom (unlayered CSS would override and kill the utility).
 *
 * lg+: sticky-cart slot is sticky side summary (not fixed overlay), so pb drops clearance.
 */
const MAIN_BOTTOM_PADDING =
  'pb-[calc(var(--customer-bottom-chrome,calc(4.75rem+env(safe-area-inset-bottom)))+var(--customer-sticky-cart-clearance,0px)+2rem)] lg:pb-4';

/** Hide-nav #main pb — recovery/* only; single-owner safe-area + clearance + buffer. */
const MAIN_BOTTOM_PADDING_HIDE_NAV =
  'pb-[calc(max(1rem,env(safe-area-inset-bottom,0px))+var(--customer-sticky-cart-clearance,0px)+2rem)] lg:pb-4';

/** Open modal (HubEmptyCartSheet / CustomerChromeTopPanel) — lives outside #main. */
const OPEN_ARIA_MODAL_DIALOG = '[role="dialog"][aria-modal="true"]';

interface CustomerAppShellBodyProps {
  readonly children?: ReactNode;
}

function CustomerAppShellBody({ children }: CustomerAppShellBodyProps): JSX.Element {
  const tenantCode = useTenantCode();
  const location = useLocation();
  const { pathname, key: locationKey } = location;
  const { session } = useCustomerSession();
  const me = useAccountMe(session !== null);
  const mainRef = useRef<HTMLElement>(null);
  useHierarchicalSystemBack();
  useBrowseVisitRecorder();
  useOidcPostAuthReturnRestore();
  usePostPayPendingAuthActionRunner();
  useOidcPopupChildHandoff();
  useOidcPopupOpenerResume();
  useDirectoryUiPreferenceHydrate();
  const depthBack = useShellDepthBack();
  const recoveryPassThrough = isCustomerRecoveryPath(pathname);
  const hideBottomNav = shouldHideBottomNav(pathname, tenantCode, {
    mustCompleteVerification: me.data?.mustCompleteVerification === true,
  });
  useEffect(() => {
    if (!hideBottomNav) {
      document.documentElement.dataset.sidebarVisible = 'true';
    } else {
      delete document.documentElement.dataset.sidebarVisible;
    }
    return () => {
      delete document.documentElement.dataset.sidebarVisible;
    };
  }, [hideBottomNav]);
  const { isMoreOpen, openMore, closeMore, moreCompact } = useMoreShell();
  const overlayBack = useOptionalOverlayBackDismiss();
  const overlayDismissRef = useRef(overlayBack?.overlayDismiss);
  useLayoutEffect(() => {
    overlayDismissRef.current = overlayBack?.overlayDismiss;
  });
  const onAccountRoute = isAccountRoute(pathname, tenantCode);

  const handleMoreClick = useCallback((): void => {
    openMore({ compact: onAccountRoute });
  }, [onAccountRoute, openMore]);

  useEffect(() => {
    if (isMoreRoute(pathname, tenantCode)) {
      return;
    }
    closeMore();
  }, [closeMore, pathname, tenantCode]);

  useEffect(() => {
    const root = document.documentElement;
    const mq = window.matchMedia('(min-width: 1024px)');

    const apply = (): void => {
      root.style.setProperty(
        '--customer-bottom-chrome',
        hideBottomNav || mq.matches ? '0px' : BOTTOM_CHROME_CLEARANCE,
      );
    };

    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      root.style.setProperty('--customer-bottom-chrome', '0px');
    };
  }, [hideBottomNav]);

  /**
   * SPA route focus → main landmark (Focus Not Obscured / route announce).
   * G4: skip when hub-empty / any aria-modal is open so #main cannot steal
   * after CustomerChromeTopPanel / sheet focus (incl. G2 Body remount).
   * Overlay/dialog reads are effect-time snapshots of this route commit —
   * overlayDismiss is read via ref so sheet close does not re-announce #main
   * over the restore target.
   */
  useEffect(() => {
    const main = mainRef.current;
    if (main === null) {
      return;
    }
    const active = document.activeElement;
    if (active !== null && active !== document.body && main.contains(active)) {
      return;
    }
    if (resolveHubEmptyCartConsumeAction(location.state) === 'open_and_clear') {
      return;
    }
    if (overlayDismissRef.current != null) {
      return;
    }
    if (document.querySelector(OPEN_ARIA_MODAL_DIALOG) !== null) {
      return;
    }
    if (active instanceof Element && active.closest(OPEN_ARIA_MODAL_DIALOG) !== null) {
      return;
    }
    main.focus({ preventScroll: true });
  }, [location.state, locationKey, pathname]);

  return (
    <ShellDepthBackProvider ownsBack>
      {/* G13 / Spec §13: no overflow-x-hidden on main (or outer) — constrain via min-w-0 so fixed header is never clipped */}
      {/*
        G5 leftover-height chain (phone-390 / not-lg): min-h-dvh + flex-1 min-h-0
        through mid → column → content → #main so % heights (e.g. map h-[60%])
        resolve against chrome leftover — not indefinite-% theater.
        lg+: default stretch (no items-start); sticky-cart keeps lg:self-start.
      */}
      <div className="flex min-h-dvh flex-col bg-[var(--color-surface-muted)]">
        <CustomerShellHeader depthBack={depthBack} />
        {/* G7: explicit lg:flex-row — sidebar + main side-by-side on desktop */}
        <div className="flex min-h-0 flex-1 lg:flex-row">
          <CustomerDesktopSidebar />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <CustomerPullToRefresh />
            <CustomerTestEconomyBanner />
            <CustomerOfflineBanner />
            <ConsentSyncFailedBanner />
            {/*
              Mobile: fixed cart floater only when cart has lines (empty → no compact bar / 0 clearance).
              lg+: main + sticky side summary (Spec §20.2) — slot collapses when empty (aria-hidden).
              G5: min-h-0 + stretch (omit lg:items-start) so #main gets definite cross-axis height.
            */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row">
              <main
                ref={mainRef}
                id="main"
                tabIndex={-1}
                className={[
                  `customer-shell-main customer-safe-area-x flex min-h-0 min-w-0 flex-1 flex-col outline-none ${CUSTOMER_CONTENT_WIDTH_CLASS} lg:max-w-none`,
                  hideBottomNav ? MAIN_BOTTOM_PADDING_HIDE_NAV : MAIN_BOTTOM_PADDING,
                ].join(' ')}
              >
                <RemountBoundary
                  disabled={recoveryPassThrough}
                  feature="shell-outlet"
                  fallback={({ onRetry, feature }) => (
                    <RouteErrorFallback
                      onRetry={onRetry}
                      feature={feature}
                      testId="customer-eb-l2-fallback"
                    />
                  )}
                >
                  <ErrorIsolationProbe feature="shell-outlet" />
                  <E2eNavigateBridgeMount />
                  <RequireContactVerificationComplete>
                    {children ?? <Outlet />}
                  </RequireContactVerificationComplete>
                </RemountBoundary>
                {/* Cookie reopen lives on Settings sheet prefs via openBanner({ force: true }) — not shell chrome. */}
              </main>

              {/* Mobile: fixed bottom floater. lg+: sticky side summary — not a floating bar over products. */}
              <div
                id="customer-sticky-cart-slot"
                className={`customer-safe-area-x pointer-events-none fixed inset-x-0 bottom-[var(--customer-bottom-chrome)] z-20 ${CUSTOMER_CONTENT_WIDTH_CLASS} lg:inset-auto lg:bottom-auto lg:z-auto lg:mx-0 lg:max-w-none lg:w-[min(22rem,100%)] lg:shrink-0 lg:self-start lg:sticky lg:top-[calc(env(safe-area-inset-top,0px)+3.5rem+0.5rem)] lg:aria-hidden:hidden`}
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        <BottomNav onMoreClick={handleMoreClick} isMoreActive={isMoreOpen} />
        <MoreDrawer open={isMoreOpen} compact={moreCompact} onClose={closeMore} />
        <AppShellChromeFooter versionLabel={APP_BUILD_LABEL} bottomChromeVar="--customer-bottom-chrome" />
        {/* Allowlisted Wave B exception — ToastHost once in shell; not a Cart/Collect portal remount. */}
        <ToastHost />
      </div>
    </ShellDepthBackProvider>
  );
}

interface CustomerAppShellProps {
  /** When set (platform `/shops`), renders children instead of the route `<Outlet />`. */
  readonly children?: ReactNode;
}

/** Retail shell — stable top header, bottom nav, More drawer, sticky-cart slot. */
export function CustomerAppShell({ children }: CustomerAppShellProps): JSX.Element {
  const tenantCode = useTenantCode();
  return (
    <MoreShellProvider>
      <CustomerShellChromeProvider>
        <OverlayBackDismissProvider>
          {/*
            Provider must wrap BOTH shell POP/depth hooks (Body) AND <Outlet/> publishers.
            Remount on tenant so in-flow snapshot cannot leak across orgs.
          */}
          <HierarchicalBackFlowProvider key={tenantCode}>
            <ShellPlaceTitleProvider>
              <DiscoveryShellLocationProvider>
                <CustomerAppShellBody>{children}</CustomerAppShellBody>
              </DiscoveryShellLocationProvider>
            </ShellPlaceTitleProvider>
          </HierarchicalBackFlowProvider>
        </OverlayBackDismissProvider>
      </CustomerShellChromeProvider>
    </MoreShellProvider>
  );
}
